import { FormDatePicker } from '@/components/form-date-picker/FormDatePicker';
import { KeyboardArrowRightOutlined } from '@mui/icons-material';
import { Box, Grid, Stack } from '@mui/material';
import { dateValidator } from '@vestwell-frontend/helpers';
import { Button, Form, FormSaveButton } from '@vestwell-frontend/ui';

import { format, isAfter, parseISO } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';

export type Period = {
  endDate?: Date | string;
  startDate?: Date | string;
};

type RateOfReturnRangeFormProps = {
  period: Period;
  change: (period: Period) => void;
  cancel: () => void;
  disabled?: boolean;
  submitTitle: string;
};

const today = format(new Date(), 'yyyy-MM-dd');

const validationSchema = yup.object().shape({
  endDate: yup.string().test({
    test: async function (value) {
      try {
        await dateValidator('End Date', {
          max: today,
          maxErrorMessage: 'End Date cannot be in the future',
          min: this.parent?.startDate,
          minErrorMessage: 'End Date cannot be before Start Date',
          required: true
        }).validate(value);

        return true;
      } catch (err) {
        return this.createError({
          message: err.message,
          path: this.path
        });
      }
    }
  }),
  startDate: dateValidator('Start Date', {
    max: today,
    required: true
  }).test('is-after-2023', 'Start Date must be after 01/01/2023', value => {
    if (!value) return true;
    return isAfter(parseISO(value), new Date('2023-01-01'));
  })
});

export const CustomRangeForm: FC<RateOfReturnRangeFormProps> = props => {
  const formContainerRef = useRef<HTMLDivElement>();
  useEffect(() => {
    formContainerRef.current.scrollIntoView();
  }, []);

  useEffect(() => {
    if (props.disabled) {
      formContainerRef.current.scrollIntoView();
    }
  }, [props.disabled]);

  const formValues = useMemo(
    () => ({
      endDate: props.period?.endDate,
      startDate: props.period?.startDate
    }),
    [props.period]
  );

  const onSubmit = useCallback(
    values => {
      props.change({
        endDate: values.endDate || undefined,
        startDate: values.startDate || undefined
      });
    },
    [props.change]
  );

  return (
    <Box m={0} p={0} ref={formContainerRef}>
      <Form
        initialValues={formValues}
        onSubmit={onSubmit}
        validateOnChange={true}
        validationSchema={validationSchema}>
        <Grid columnGap={2.5} container mt={-1} pt={0} wrap='nowrap'>
          <Grid item overflow='hidden' pt={1} sm={6}>
            <FormDatePicker
              data-testid='start-date-picker'
              disabled={props.disabled}
              label='Start Date'
              name='startDate'
            />
          </Grid>
          <Grid item overflow='hidden' pt={1} sm={6}>
            <FormDatePicker
              data-testid='end-date-picker'
              disabled={props.disabled}
              label='End Date'
              name='endDate'
            />
          </Grid>
        </Grid>
        <Stack direction='row' justifyContent='space-between' pt={3.5}>
          <Button onClick={props.cancel} variant='text'>
            Cancel
          </Button>
          <FormSaveButton
            data-testid='calculate-button'
            disabled={props.disabled}
            endIcon={<KeyboardArrowRightOutlined fontSize='small' />}
            ensureDirty>
            {props.submitTitle}
          </FormSaveButton>
        </Stack>
      </Form>
    </Box>
  );
};
